import { cloneElement, forwardRef } from 'react';
import clsx from 'clsx';
import { ListItem, ListItemIcon, ListItemText, Tooltip } from '@bymason-private/legacy-pattern-components';
import {
  ControllerFontType,
  ControllerLeftNavColor,
  makeStyles,
} from '@bymason-private/controller-v1-theme-provider';

interface NavListItemProps {
  active?: boolean;
  collapse: boolean;
  dense?: boolean;
  icon: JSX.Element;
  iconType?: 'avatar' | 'svg';
  colorOverride?: string;
  onClick?: () => void;
  text: string;
}

const useStyles = makeStyles((theme) => ({
  itemRoot: {
    marginBottom: theme.spacing(1.75),
    paddingRight: theme.spacing(1),
  },
  itemRootForAvatar: {
    paddingLeft: theme.spacing(1.5),
  },
  itemRootForDense: {
    marginBottom: 0,
  },
  itemRootCollapse: {
    paddingLeft: theme.spacing(1),
    paddingRight: 0,
  },
  itemChildRoot: {
    alignItems: 'center',
    borderBottom: '2px solid',
    borderBottomColor: 'transparent',
    color: ControllerLeftNavColor.DefaultFg,
    display: 'flex',
    flexDirection: 'row',
    padding: theme.spacing(0.5, 1),
    transition: 'all .35s ease-out',
    transitionProperty: 'border-bottom-color, color',
    '&:hover': {
      borderBottomColor: ControllerLeftNavColor.HoverFg,
      color: ControllerLeftNavColor.HoverFg,
    },
  },
  itemChildRootActive: {
    borderBottomColor: ControllerLeftNavColor.ActiveFg,
    color: ControllerLeftNavColor.ActiveFg,
  },
  itemChildRootCollapse: {
    paddingRight: 0,
  },
  itemChildRootAvatarCollapse: {
    paddingLeft: theme.spacing(0.25),
    paddingRight: theme.spacing(0.5),
  },
  itemIconRoot: {
    color: 'inherit',
    marginRight: theme.spacing(1.5),
    minWidth: 0,
  },
  itemIconRootCollapse: {
    marginRight: theme.spacing(1),
  },
  itemAvatarRoot: {
    marginRight: theme.spacing(1),
    minWidth: 0,
  },
  itemAvatarRootCollapse: {
    marginRight: theme.spacing(0),
  },
  itemAvatar: {
    height: 24,
    width: 24,
  },
  itemTextRoot: {
    margin: 0,
  },
}));

export default forwardRef<HTMLDivElement, NavListItemProps>(function NavListItem(
  { active, collapse, dense, icon, iconType = 'svg', colorOverride, onClick, text }: NavListItemProps,
  ref
) {
  const classes = useStyles();

  return (
    <ListItem
      button
      classes={{
        root: clsx(
          classes.itemRoot,
          iconType === 'avatar' && classes.itemRootForAvatar,
          dense && classes.itemRootForDense,
          collapse && classes.itemRootCollapse
        ),
      }}
      key={text}
      onClick={onClick}
    >
      <div>
        <Tooltip arrow disableHoverListener={!collapse} title={text} placement='right'>
          <div
            style={{ color: colorOverride }}
            className={clsx(
              classes.itemChildRoot,
              active && classes.itemChildRootActive,
              collapse && classes.itemChildRootCollapse,
              iconType === 'avatar' && collapse && classes.itemChildRootAvatarCollapse
            )}
          >
            <ListItemIcon
              classes={{
                root:
                  iconType === 'svg'
                    ? clsx(classes.itemIconRoot, collapse && classes.itemIconRootCollapse)
                    : clsx(classes.itemAvatarRoot, collapse && classes.itemAvatarRootCollapse),
              }}
              ref={collapse ? ref : undefined}
            >
              {cloneElement(icon, { className: iconType !== 'svg' ? classes.itemAvatar : undefined })}
            </ListItemIcon>
            {!collapse && (
              <ListItemText
                classes={{ root: classes.itemTextRoot }}
                primary={text}
                primaryTypographyProps={{
                  variant: dense ? ControllerFontType.Text300M : ControllerFontType.Heading300B,
                  sx: { lineHeight: 1.25 },
                }}
                ref={!collapse ? ref : undefined}
              />
            )}
          </div>
        </Tooltip>
      </div>
    </ListItem>
  );
});
